import type { QueryParamsList, QueryParamsRetrieve } from '../query'
import type { ListResponse, Resource, ResourceCreate, ResourceId, ResourceRel, ResourceSort, /* ResourceFilter */ ResourcesConfig, ResourceUpdate, } from '../resource'
import { ApiResource } from '../resource'
import type { Attachment } from './attachments'
import type { EventStore } from './event_stores'
import type { Event } from './events'
import type { Market, MarketType } from './markets'
import type { Tag, TagType } from './tags'


type SkuOptionType = 'sku_options'
type SkuOptionRel = ResourceRel & { type: SkuOptionType }
type MarketRel = ResourceRel & { type: MarketType }
type TagRel = ResourceRel & { type: TagType }


export type SkuOptionSort = Pick<SkuOption, 'id' | 'name' | 'currency_code' | 'price_amount_cents' | 'delay_hours' | 'delay_days'> & ResourceSort
// export type SkuOptionFilter = Pick<SkuOption, 'id' | 'name' | 'currency_code' | 'description' | 'price_amount_cents' | 'delay_hours' | 'delay_days'> & ResourceFilter


interface SkuOption extends Resource {
	
	readonly type: SkuOptionType

	/** 
	 * The SKU option's internal name.
	 * @example ```"Embossing"```
	 */
	name: string
	/** 
	 * The international 3-letter currency code as defined by the ISO 4217 standard.
	 * @example ```"EUR"```
	 */
	currency_code?: string | null
	/** 
	 * An internal description of the SKU option.
	 * @example ```"Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."```
	 */
	description?: string | null
	/** 
	 * The price of this shipping method, in cents.
	 * @example ```1000```
	 */
	price_amount_cents?: number | null
	/** 
	 * The price of this shipping method, float.
	 * @example ```10```
	 */
	price_amount_float?: number | null
	/** 
	 * The price of this shipping method, formatted.
	 * @example ```"€10,00"```
	 */
	formatted_price_amount?: string | null
	/** 
	 * The delay time (in hours) that should be added to the delivery lead time when this option is purchased.
	 * @example ```48```
	 */
	delay_hours?: number | null
	/** 
	 * The delay time, in days (rounded).
	 * @example ```2```
	 */
	delay_days?: number | null
	/** 
	 * The regex that will be evaluated to match the SKU codes, max size is 5000.
	 * @example ```"^(A|B).*$"```
	 */
	sku_code_regex?: string | null

	market?: Market | null
	attachments?: Attachment[] | null
	events?: Event[] | null
	tags?: Tag[] | null
	event_stores?: EventStore[] | null

}


interface SkuOptionCreate extends ResourceCreate {
	
	/** 
	 * The SKU option's internal name.
	 * @example ```"Embossing"```
	 */
	name: string
	/** 
	 * The international 3-letter currency code as defined by the ISO 4217 standard.
	 * @example ```"EUR"```
	 */
	currency_code?: string | null
	/** 
	 * An internal description of the SKU option.
	 * @example ```"Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."```
	 */
	description?: string | null
	/** 
	 * The price of this shipping method, in cents.
	 * @example ```1000```
	 */
	price_amount_cents?: number | null
	/** 
	 * The delay time (in hours) that should be added to the delivery lead time when this option is purchased.
	 * @example ```48```
	 */
	delay_hours?: number | null
	/** 
	 * The regex that will be evaluated to match the SKU codes, max size is 5000.
	 * @example ```"^(A|B).*$"```
	 */
	sku_code_regex?: string | null

	market?: MarketRel | null
	tags?: TagRel[] | null

}


interface SkuOptionUpdate extends ResourceUpdate {
	
	/** 
	 * The SKU option's internal name.
	 * @example ```"Embossing"```
	 */
	name?: string | null
	/** 
	 * The international 3-letter currency code as defined by the ISO 4217 standard.
	 * @example ```"EUR"```
	 */
	currency_code?: string | null
	/** 
	 * An internal description of the SKU option.
	 * @example ```"Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."```
	 */
	description?: string | null
	/** 
	 * The price of this shipping method, in cents.
	 * @example ```1000```
	 */
	price_amount_cents?: number | null
	/** 
	 * The delay time (in hours) that should be added to the delivery lead time when this option is purchased.
	 * @example ```48```
	 */
	delay_hours?: number | null
	/** 
	 * The regex that will be evaluated to match the SKU codes, max size is 5000.
	 * @example ```"^(A|B).*$"```
	 */
	sku_code_regex?: string | null
	/** 
	 * Comma separated list of tags to be added. Duplicates, invalid and non existing ones are discarded. Cannot be passed by sales channels.
	 */
	_add_tags?: string | null
	/** 
	 * Comma separated list of tags to be removed. Duplicates, invalid and non existing ones are discarded. Cannot be passed by sales channels.
	 */
	_remove_tags?: string | null

	market?: MarketRel | null
	tags?: TagRel[] | null

}


class SkuOptions extends ApiResource<SkuOption> {

	static readonly TYPE: SkuOptionType = 'sku_options' as const

	async create(resource: SkuOptionCreate, params?: QueryParamsRetrieve<SkuOption>, options?: ResourcesConfig): Promise<SkuOption> {
		return this.resources.create<SkuOptionCreate, SkuOption>({ ...resource, type: SkuOptions.TYPE }, params, options)
	}

	async update(resource: SkuOptionUpdate, params?: QueryParamsRetrieve<SkuOption>, options?: ResourcesConfig): Promise<SkuOption> {
		return this.resources.update<SkuOptionUpdate, SkuOption>({ ...resource, type: SkuOptions.TYPE }, params, options)
	}

	async delete(id: string | ResourceId, options?: ResourcesConfig): Promise<void> {
		await this.resources.delete((typeof id === 'string')? { id, type: SkuOptions.TYPE } : id, options)
	}

	async market(skuOptionId: string | SkuOption, params?: QueryParamsRetrieve<Market>, options?: ResourcesConfig): Promise<Market> {
		const _skuOptionId = (skuOptionId as SkuOption).id || skuOptionId as string
		return this.resources.fetch<Market>({ type: 'markets' }, `sku_options/${_skuOptionId}/market`, params, options) as unknown as Market
	}

	async attachments(skuOptionId: string | SkuOption, params?: QueryParamsList<Attachment>, options?: ResourcesConfig): Promise<ListResponse<Attachment>> {
		const _skuOptionId = (skuOptionId as SkuOption).id || skuOptionId as string
		return this.resources.fetch<Attachment>({ type: 'attachments' }, `sku_options/${_skuOptionId}/attachments`, params, options) as unknown as ListResponse<Attachment>
	}

	async events(skuOptionId: string | SkuOption, params?: QueryParamsList<Event>, options?: ResourcesConfig): Promise<ListResponse<Event>> {
		const _skuOptionId = (skuOptionId as SkuOption).id || skuOptionId as string
		return this.resources.fetch<Event>({ type: 'events' }, `sku_options/${_skuOptionId}/events`, params, options) as unknown as ListResponse<Event>
	}

	async tags(skuOptionId: string | SkuOption, params?: QueryParamsList<Tag>, options?: ResourcesConfig): Promise<ListResponse<Tag>> {
		const _skuOptionId = (skuOptionId as SkuOption).id || skuOptionId as string
		return this.resources.fetch<Tag>({ type: 'tags' }, `sku_options/${_skuOptionId}/tags`, params, options) as unknown as ListResponse<Tag>
	}

	async event_stores(skuOptionId: string | SkuOption, params?: QueryParamsList<EventStore>, options?: ResourcesConfig): Promise<ListResponse<EventStore>> {
		const _skuOptionId = (skuOptionId as SkuOption).id || skuOptionId as string
		return this.resources.fetch<EventStore>({ type: 'event_stores' }, `sku_options/${_skuOptionId}/event_stores`, params, options) as unknown as ListResponse<EventStore>
	}

	async _add_tags(id: string | SkuOption, triggerValue: string, params?: QueryParamsRetrieve<SkuOption>, options?: ResourcesConfig): Promise<SkuOption> {
		return this.resources.update<SkuOptionUpdate, SkuOption>({ id: (typeof id === 'string')? id: id.id, type: SkuOptions.TYPE, _add_tags: triggerValue }, params, options)
	}

	async _remove_tags(id: string | SkuOption, triggerValue: string, params?: QueryParamsRetrieve<SkuOption>, options?: ResourcesConfig): Promise<SkuOption> {
		return this.resources.update<SkuOptionUpdate, SkuOption>({ id: (typeof id === 'string')? id: id.id, type: SkuOptions.TYPE, _remove_tags: triggerValue }, params, options)
	}


	isSkuOption(resource: any): resource is SkuOption {
		return resource.type && (resource.type === SkuOptions.TYPE)
	}


	relationship(id: string | ResourceId | null): SkuOptionRel {
		return super.relationshipOneToOne<SkuOptionRel>(id)
	}

	relationshipToMany(...ids: string[]): SkuOptionRel[] {
		return super.relationshipOneToMany<SkuOptionRel>(...ids)
	}


	type(): SkuOptionType {
		return SkuOptions.TYPE
	}

}


const instance = new SkuOptions()
export default instance

export type { SkuOption, SkuOptionCreate, SkuOptions, SkuOptionType, SkuOptionUpdate }
